import React, { useState } from "react";

import { faEllipsisVertical } from "@performyard/fontawesome/pro-solid-svg-icons";
import type { MeetingStatus } from "@performyard/models";
import { ButtonIcon, Icon, Text } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";

import { AddVideoConferencingModal } from "../../MeetingsTable/components/modals/AddVideoConferencingModal";
import FloatingDropdown from "./FloatingDropdown";

import { useEditWorkspaceMeeting } from "../../../../api/hooks";
import { useVideoConferencingFeatureEnabled } from "../../../../api/hooks/defs/meetings-integrations";
import { useIsNonOwnerSeriesVideoConference, useVideoConferencingEligibility } from "../../MeetingsTable/hooks";
import type { MenuOption } from "../hooks";
import { useCalendarIntegration, useMeetingOptions } from "../hooks";

import { config } from "../config";
import { StatusConfirmationModal } from "../modals";

type ModalState =
	| { type: "none" }
	| { type: "remove-from-calendar"; isLoading: boolean }
	| { type: "remove-video-conferencing"; isLoading: boolean }
	| { type: "add-video-conferencing" }
	| { type: "status-change"; isLoading: boolean; status: MeetingStatus };

type MeetingOptionsDropdownProps = {
	meeting: MeetingModelJson;
	setEditingMeeting: (meeting: MeetingModelJson) => void;
};

/**
 * Dropdown menu for meeting options: edit, calendar integration, and status changes.
 */
const MeetingOptionsDropdown = ({
	meeting,
	setEditingMeeting,
}: MeetingOptionsDropdownProps): React.ReactElement | null => {
	const [isOpen, setIsOpen] = useState(false);
	const [modalState, setModalState] = useState<ModalState>({ type: "none" });

	const { mutate: editMeeting } = useEditWorkspaceMeeting();

	const { isCalendarIntegrationEnabled, hasCalendarEvent, isCalendarMismatch, isNonOwnerSeriesEvent } =
		useCalendarIntegration({
			meeting,
		});
	const videoConferencingFeatureEnabled = useVideoConferencingFeatureEnabled();
	const {
		eligible: isVideoConferencingEligible,
		adminEnabled: isVideoConferencingAdminEnabled,
		connectedProviders,
	} = useVideoConferencingEligibility();

	const isNonOwnerSeriesVideoConference = useIsNonOwnerSeriesVideoConference(meeting);

	const { options } = useMeetingOptions({
		meeting,
		isCalendarIntegrationEnabled,
		hasCalendarEvent,
		isCalendarMismatch,
		isNonOwnerSeriesEvent,
		isNonOwnerSeriesVideoConference,
		isVideoConferencingFeatureEnabled: videoConferencingFeatureEnabled,
		isVideoConferencingEligible,
		isVideoConferencingAdminEnabled,
		connectedProviders,
	});

	if (options.length === 0) {
		return null;
	}

	const handleOptionSelect = (option: { id: string }): void => {
		const selectedOption = options.find((o) => o.id === option.id);
		if (!selectedOption) return;

		if (selectedOption.action === "edit") {
			setEditingMeeting(meeting);
		} else if (selectedOption.action === "calendar") {
			if (selectedOption.id === "add-to-calendar") {
				editMeeting({
					meetingId: meeting._id,
					data: { addToCalendar: true },
				});
			} else if (selectedOption.id === "remove-from-calendar") {
				setModalState({ type: "remove-from-calendar", isLoading: false });
			}
		} else if (selectedOption.action === "video-conferencing") {
			if (selectedOption.id === "remove-video-conferencing") {
				setModalState({ type: "remove-video-conferencing", isLoading: false });
			} else if (selectedOption.id === "add-video-conferencing") {
				// A single connected provider attaches directly; several open the provider picker
				// (PY-12435 — mirrors the row dropdown).
				if (connectedProviders.length === 1) {
					editMeeting({
						meetingId: meeting._id,
						data: { videoConferencingProvider: connectedProviders[0].provider },
					});
				} else {
					setModalState({ type: "add-video-conferencing" });
				}
			}
		} else if (selectedOption.action === "status") {
			const status = selectedOption.id as MeetingStatus;
			if (["canceled", "completed", "archived"].includes(status)) {
				setModalState({ type: "status-change", isLoading: false, status });
			} else {
				editMeeting({ meetingId: meeting._id, data: { status } });
			}
		}
	};

	const handleConfirmStatusChange = (options: {
		removeFromCalendar?: boolean;
		removeVideoConferencing?: boolean;
	}): void => {
		if (modalState.type === "status-change") {
			setModalState({ type: "status-change", isLoading: true, status: modalState.status });

			const updateData: {
				status: MeetingStatus;
				removeFromCalendar?: boolean;
				removeVideoConferencing?: boolean;
			} = { status: modalState.status };

			if (modalState.status === "canceled") {
				if (options.removeFromCalendar) updateData.removeFromCalendar = true;
				if (options.removeVideoConferencing) updateData.removeVideoConferencing = true;
			}

			editMeeting(
				{ meetingId: meeting._id, data: updateData },
				{
					onSuccess: () => setModalState({ type: "none" }),
					onError: () =>
						setModalState({ type: "status-change", isLoading: false, status: modalState.status }),
				}
			);
		}
	};

	const handleRemoveFromCalendar = (): void => {
		setModalState({ type: "remove-from-calendar", isLoading: true });

		editMeeting(
			{ meetingId: meeting._id, data: { removeFromCalendar: true } },
			{
				onSuccess: () => setModalState({ type: "none" }),
				onError: () => setModalState({ type: "remove-from-calendar", isLoading: false }),
			}
		);
	};

	const handleRemoveVideoConferencing = (): void => {
		setModalState({ type: "remove-video-conferencing", isLoading: true });

		editMeeting(
			{ meetingId: meeting._id, data: { removeVideoConferencing: true } },
			{
				onSuccess: () => setModalState({ type: "none" }),
				onError: () => setModalState({ type: "remove-video-conferencing", isLoading: false }),
			}
		);
	};

	const dropdownOptions = options.map((option: MenuOption) => ({
		id: option.id,
		label: (
			<Text size="paragraph-3" weight="regular" color="neutral-5">
				{option.label}
			</Text>
		),
		icon: <Icon icon={option.icon} size={config.iconSize.medium} color="neutral-7" />,
	}));

	const trigger = (
		<ButtonIcon
			variant="outline"
			color="neutral"
			size={32}
			icon={faEllipsisVertical}
			aria-label="Meeting options"
		/>
	);

	return (
		<>
			<FloatingDropdown
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				trigger={trigger}
				options={dropdownOptions}
				onSelect={handleOptionSelect}
				width={config.layout.dropdownMenuWidth}
				zIndex={config.zIndex.dropdown}
				optionClassName="px-2 py-2 whitespace-nowrap text-neutral-4 hover:text-neutral-9"
			/>

			<StatusConfirmationModal
				isOpen={
					modalState.type === "status-change" ||
					modalState.type === "remove-from-calendar" ||
					modalState.type === "remove-video-conferencing"
				}
				isLoading={
					(modalState.type === "status-change" ||
						modalState.type === "remove-from-calendar" ||
						modalState.type === "remove-video-conferencing") &&
					modalState.isLoading
				}
				status={modalState.type === "status-change" ? modalState.status : null}
				action={
					modalState.type === "remove-from-calendar"
						? "remove-from-calendar"
						: modalState.type === "remove-video-conferencing"
							? "remove-video-conferencing"
							: "status-change"
				}
				onClose={() => setModalState({ type: "none" })}
				onConfirm={
					modalState.type === "remove-from-calendar"
						? handleRemoveFromCalendar
						: modalState.type === "remove-video-conferencing"
							? handleRemoveVideoConferencing
							: handleConfirmStatusChange
				}
				hasConnectedCalendarEvent={
					hasCalendarEvent &&
					!isCalendarMismatch &&
					!isNonOwnerSeriesEvent &&
					(modalState.type === "remove-from-calendar" ||
						(modalState.type === "status-change" && modalState.status === "canceled"))
				}
				hasConnectedVideoConferencing={
					videoConferencingFeatureEnabled &&
					!!meeting.videoConference &&
					!isNonOwnerSeriesVideoConference &&
					modalState.type === "status-change" &&
					modalState.status === "canceled"
				}
			/>

			<AddVideoConferencingModal
				isOpen={modalState.type === "add-video-conferencing"}
				onClose={() => setModalState({ type: "none" })}
				meetingId={meeting._id}
				connectedProviders={connectedProviders}
			/>
		</>
	);
};

export default MeetingOptionsDropdown;
